import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, CircularProgress } from "@mui/material";
import {
  ArrowBack,
  EmailOutlined,
  PersonAddAlt1Outlined,
  PersonOutline,
  PhoneOutlined,
  PrintOutlined,
  WhatsApp,
} from "@mui/icons-material";
import ConfirmDialog from "components/Modals/ConfirmDialog";
import { P } from "const/permissions";
import { usePermission } from "hooks/usePermissions";
import { convertPrEnquiry, fetchPrEnquiry } from "services/prEnquiryApi";
import { clientProfilePath } from "views/admin/Referral/ClientProfileLink";
import {
  PR_PROFILE_SECTIONS,
  asList,
  daysUntil,
  describeDays,
  hasValue,
  readEnquiry,
  visaUrgency,
} from "./prEnquiryRecord";

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
};

const initials = (name = "") =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("") || "?";

const Card = ({ title, children, className = "" }) => (
  <section className={`rounded-xl border border-app-border bg-app-surface shadow-sm ${className}`}>
    {title ? (
      <h3 className="m-0 border-b border-app-border px-5 py-3 text-sm font-semibold text-app-text">
        {title}
      </h3>
    ) : null}
    <div className="px-5 py-4">{children}</div>
  </section>
);

const TILE_TONES = {
  expired: "border-rose-200 bg-rose-50 text-rose-800",
  urgent: "border-rose-200 bg-rose-50 text-rose-800",
  soon: "border-amber-200 bg-amber-50 text-amber-800",
  ok: "border-emerald-200 bg-emerald-50 text-emerald-800",
  neutral: "border-app-border bg-app-surface text-app-text",
};

const Tile = ({ label, value, hint, tone = "neutral" }) => (
  <div className={`rounded-xl border px-4 py-3 ${TILE_TONES[tone] || TILE_TONES.neutral}`}>
    <p className="m-0 text-[11px] font-semibold uppercase tracking-wide opacity-70">{label}</p>
    <p className="m-0 mt-1 text-lg font-bold leading-tight">{value || "—"}</p>
    {hint ? <p className="m-0 mt-0.5 text-xs opacity-80">{hint}</p> : null}
  </div>
);

const Chip = ({ children }) => (
  <span className="rounded-full border border-primary px-2.5 py-0.5 text-xs font-medium text-primary">
    {children}
  </span>
);

const FieldList = ({ fields, enquiry }) => {
  const shown = fields.filter((field) => hasValue(enquiry[field.key]));
  if (!shown.length) return <p className="m-0 text-sm text-app-text-muted">Not answered.</p>;
  return (
    <dl className="m-0 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
      {shown.map((field) => (
        <div key={field.key} className={field.chips ? "sm:col-span-2" : ""}>
          <dt className="text-xs text-app-text-secondary">{field.label}</dt>
          <dd className="m-0 mt-0.5 text-sm text-app-text">
            {field.chips ? (
              <span className="flex flex-wrap gap-1.5">
                {asList(enquiry[field.key]).map((value) => (
                  <Chip key={value}>{value}</Chip>
                ))}
              </span>
            ) : field.date ? (
              formatDate(enquiry[field.key])
            ) : (
              asList(enquiry[field.key]).join(", ")
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
};

const JobList = ({ title, jobs, total }) => (
  <div>
    <div className="mb-2 flex items-baseline justify-between gap-2">
      <p className="m-0 text-sm font-semibold text-app-text">{title}</p>
      <span className="text-xs text-app-text-secondary">{jobs.length ? total : "None"}</span>
    </div>
    {jobs.length ? (
      <ol className="m-0 list-none space-y-2 border-l-2 border-app-border p-0 pl-4">
        {jobs.map((job, index) => {
          const [role, ...rest] = String(job).split(" · ");
          return (
            <li key={index} className="relative">
              <span className="absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-app-surface bg-primary" />
              <p className="m-0 text-sm font-medium text-app-text">{role}</p>
              {rest.length ? (
                <p className="m-0 text-xs text-app-text-secondary">{rest.join(" · ")}</p>
              ) : null}
            </li>
          );
        })}
      </ol>
    ) : (
      <p className="m-0 text-sm text-app-text-muted">No jobs listed.</p>
    )}
  </div>
);

/** One PR enquiry laid out as a profile: who they are, where they stand, and what they want. */
const PrEnquiryViewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [confirmConvert, setConfirmConvert] = useState(false);
  const canViewClient = usePermission(P.CLIENTS.READ);

  const { data, isLoading, isError, refetch } = useQuery(
    ["pr-enquiry", id],
    () => fetchPrEnquiry(id),
    {
      enabled: Boolean(id),
      refetchOnWindowFocus: false,
    }
  );

  const { mutate: convert, isLoading: converting } = useMutation(() => convertPrEnquiry(id), {
    onSuccess: (converted) => {
      setConfirmConvert(false);
      toast.success(`${converted?.name || "Enquiry"} converted to a client`);
      refetch();
    },
    onError: (error) => {
      setConfirmConvert(false);
      toast.error(
        error?.response?.data?.errorMessage ||
          error?.response?.data?.message ||
          "Could not convert this enquiry"
      );
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-24">
        <CircularProgress />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="mx-4 mt-6 rounded-xl border border-app-border bg-app-surface p-8 text-center">
        <p className="m-0 font-semibold text-app-text">Couldn't load this enquiry</p>
        <p className="m-0 mt-1 text-sm text-app-text-secondary">It may have been deleted.</p>
        <Button sx={{ mt: 2 }} onClick={() => navigate("/admin/client/pr-enquiries")}>
          Back to PR enquiries
        </Button>
      </div>
    );
  }

  const enquiry = readEnquiry(data);
  const visaDays = daysUntil(enquiry.visaExpiry);
  const urgency = visaUrgency(visaDays);
  const onshore = asList(enquiry.onshoreJobs);
  const offshore = asList(enquiry.offshoreJobs);
  const phone = String(enquiry.number || "").replace(/[^\d+]/g, "");
  const converted = Boolean(enquiry.converted);
  const clientProfileLink =
    converted && enquiry.convertedClientId && canViewClient
      ? clientProfilePath(enquiry.convertedClientId)
      : null;

  return (
    <div className="mt-4 space-y-4 px-4 pb-10">
      <div className="flex flex-wrap items-center justify-between gap-2 print:hidden">
        <Link
          to="/admin/client/pr-enquiries"
          className="inline-flex items-center gap-1 text-sm font-medium text-primary no-underline hover:underline"
        >
          <ArrowBack fontSize="small" /> PR enquiries
        </Link>
        <Button size="small" startIcon={<PrintOutlined />} onClick={() => window.print()}>
          Print
        </Button>
      </div>

      <Card>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex min-w-0 items-center gap-4">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary text-lg font-bold text-white">
              {initials(enquiry.name)}
            </span>
            <div className="min-w-0">
              <h2 className="m-0 truncate text-xl font-bold text-app-text">
                {clientProfileLink ? (
                  <Link
                    to={clientProfileLink}
                    title="Open client profile"
                    className="text-app-text no-underline hover:underline"
                  >
                    {enquiry.name || "Unnamed"}
                  </Link>
                ) : (
                  enquiry.name || "Unnamed"
                )}
              </h2>
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                {enquiry.visaStatus ? <Chip>{enquiry.visaStatus}</Chip> : null}
                {enquiry.inAustralia ? (
                  <Chip>{enquiry.inAustralia === "Yes" ? "Onshore" : "Offshore"}</Chip>
                ) : null}
                <span
                  className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                    converted ? "bg-emerald-50 text-emerald-700" : "bg-sky-50 text-sky-700"
                  }`}
                >
                  {converted ? "Client" : "New enquiry"}
                </span>
              </div>
              <p className="m-0 mt-1.5 text-xs text-app-text-secondary">
                Received {formatDate(enquiry.createdAt) || "—"}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 print:hidden">
            {enquiry.email ? (
              <Button
                size="small"
                variant="outlined"
                startIcon={<EmailOutlined />}
                href={`https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(
                  enquiry.email
                )}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Email
              </Button>
            ) : null}
            {phone ? (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PhoneOutlined />}
                  href={`tel:${phone}`}
                >
                  Call
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<WhatsApp />}
                  href={`https://wa.me/${phone.replace(/^\+/, "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  WhatsApp
                </Button>
              </>
            ) : null}
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tile
          label="Visa valid until"
          value={formatDate(enquiry.visaExpiry)}
          hint={describeDays(visaDays)}
          tone={urgency === "unknown" ? "neutral" : urgency}
        />
        <Tile
          label="Onshore experience"
          value={onshore.length ? enquiry.onshoreExperienceTotal : "None"}
        />
        <Tile
          label="Offshore experience"
          value={offshore.length ? enquiry.offshoreExperienceTotal : "None"}
        />
        <Tile
          label="English"
          value={[enquiry.englishTest, enquiry.englishScore].filter(Boolean).join(" ")}
          hint={enquiry.englishTestDate ? `Tested ${formatDate(enquiry.englishTestDate)}` : ""}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <div className="space-y-4">
          <Card title="Their plan">
            {enquiry.futurePlan ? (
              <blockquote className="m-0 border-l-4 border-primary pl-4 text-sm italic text-app-text">
                {enquiry.futurePlan}
              </blockquote>
            ) : (
              <p className="m-0 text-sm text-app-text-muted">No plan written.</p>
            )}
            <div className="mt-4">
              <FieldList
                fields={PR_PROFILE_SECTIONS.find((s) => s.id === "goals").fields}
                enquiry={enquiry}
              />
            </div>
          </Card>

          <Card title="Work experience">
            <div className="grid gap-6 sm:grid-cols-2">
              <JobList title="In Australia" jobs={onshore} total={enquiry.onshoreExperienceTotal} />
              <JobList title="Overseas" jobs={offshore} total={enquiry.offshoreExperienceTotal} />
            </div>
          </Card>

          {enquiry.comments ? (
            <Card title="Anything else they told us">
              <p className="m-0 whitespace-pre-wrap text-sm text-app-text">{enquiry.comments}</p>
            </Card>
          ) : null}
        </div>

        <div className="space-y-4">
          {PR_PROFILE_SECTIONS.filter((section) => section.id !== "goals").map((section) => (
            <Card key={section.id} title={section.title}>
              <FieldList fields={section.fields} enquiry={enquiry} />
            </Card>
          ))}
        </div>
      </div>

      <ConfirmDialog
        open={confirmConvert}
        title="Convert to client?"
        message={`${
          enquiry.name || "This enquiry"
        } will be added to your clients using these details${
          enquiry.email ? ` (${enquiry.email})` : ""
        }. If they are already a client, this links to the existing record instead.`}
        confirmLabel="Convert"
        confirmColor="success"
        loading={converting}
        onConfirm={() => convert()}
        onCancel={() => setConfirmConvert(false)}
      />
    </div>
  );
};

export default PrEnquiryViewPage;
